import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
    useTransition,
    memo,
} from 'react';
import { FaRedoAlt } from 'react-icons/fa';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import api from '../services/api';
import AnuncioModal from '../components/AnuncioModal';
import CoverflowSlideshow from './CoverflowSlideshow';
import './Dashboard.css';

import {
    WiDaySunny, WiNightClear, WiDayCloudy, WiNightAltCloudy, WiCloud,
    WiCloudy, WiDayRain, WiNightAltRain, WiDayShowers, WiNightAltShowers,
    WiDayThunderstorm, WiNightAltThunderstorm, WiSnow, WiFog,
} from 'react-icons/wi';
import { FiSearch, FiMapPin, FiArrowLeft } from 'react-icons/fi';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

/* =========================================================
   Agenda — Configurações de Tipo
   ========================================================= */
const TIPO_CONFIG = {
    evento  : { label: 'Evento',   emoji: '📌', bg: '#ede9fe', color: '#7c3aed' },
    reuniao : { label: 'Reunião',  emoji: '👥', bg: '#dbeafe', color: '#1d4ed8' },
    lembrete: { label: 'Lembrete', emoji: '🔔', bg: '#fef3c7', color: '#b45309' },
    prazo   : { label: 'Prazo',    emoji: '⏰', bg: '#fee2e2', color: '#dc2626' },
    pessoal : { label: 'Pessoal',  emoji: '🏠', bg: '#d1fae5', color: '#065f46' },
};

const CORES_PALETA = [
    '#4f46e5', '#7c3aed', '#db2777', '#dc2626',
    '#ea580c', '#d97706', '#16a34a', '#0891b2',
];

/* =========================================================
   Utils & Hooks
   ========================================================= */
const toISODateLocal = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).toLocaleDateString('en-CA');

/**
 * Com TZ=America/Sao_Paulo no servidor, mysql2 converte o datetime
 * armazenado (horário local) para UTC ao retornar (ex: 20:10 BRT → 23:10Z).
 * O browser em BRT converte de volta corretamente: new Date('23:10Z') → 20:10.
 * NÃO usar timeZone:'UTC' aqui — o ciclo já está correto.
 */
const fmtHora = (dtStr) => {
    if (!dtStr) return '';
    return new Date(dtStr).toLocaleTimeString('pt-BR', {
        hour  : '2-digit',
        minute: '2-digit',
    });
};

function useDebouncedValue(value, delay = 300) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

function useOnClickOutside(ref, handler) {
    const handlerRef = useRef(handler);
    useEffect(() => { handlerRef.current = handler; }, [handler]);
    useEffect(() => {
        const onDown = (e) => {
            if (!ref.current || ref.current.contains(e.target)) return;
            handlerRef.current?.(e);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [ref]);
}

/* =========================================================
   useProximosEventos — Hook com arquitetura de polling em camadas
   ─────────────────────────────────────────────────────────
   Camada 1: Disparo imediato via window event 'agenda_updated'
             (zero latência após criar/deletar evento)
   Camada 2: visibilitychange — re-busca ao voltar à aba
   Camada 3: Polling a cada 30s — consistência garantida
   ========================================================= */
function useProximosEventos() {
    const [proximos, setProximos] = useState([]);

    const fetchProximos = useCallback(async () => {
        try {
            const { data } = await api.get('/api/agenda/proximos');
            setProximos(Array.isArray(data) ? data : []);
        } catch (err) {
            // Loga sem quebrar o componente — agenda não é crítica
            console.warn('[ProximosEventos] Fetch falhou:', err?.response?.status ?? err?.message);
        }
    }, []);

    useEffect(() => {
        // Camada 1: fetch imediato na montagem
        fetchProximos();

        // Camada 2: polling a cada 30 segundos
        const polling = setInterval(fetchProximos, 30_000);

        // Camada 3: re-fetch ao voltar para a aba
        const onVisibility = () => {
            if (document.visibilityState === 'visible') fetchProximos();
        };
        document.addEventListener('visibilitychange', onVisibility);

        // Camada 4: evento customizado disparado pelo próprio Dashboard
        //           após criar, deletar ou atualizar um evento da agenda
        const onAgendaUpdate = () => fetchProximos();
        window.addEventListener('agenda_updated', onAgendaUpdate);

        return () => {
            clearInterval(polling);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('agenda_updated', onAgendaUpdate);
        };
    }, [fetchProximos]);

    return { proximos };
}

/** Dispara o evento global para sincronizar o ProximoEventoWidget */
const notifyAgendaUpdate = () =>
    window.dispatchEvent(new CustomEvent('agenda_updated'));

/* =========================================================
   AdminTenantBanner
   ========================================================= */
function AdminTenantBanner({ tenantName = 'cliente' }) {
    const [busy, setBusy] = useState(false);

    const handleBack = useCallback(async () => {
        if (busy) return;
        setBusy(true);
        try {
            // [FASE 3] Volta à visão global via clear-tenant: zera o active_tenant_id
            // na sessão do admin (cookie). Sem restaurar token, sem adminOriginalToken.
            await api.post('/api/auth/clear-tenant');
        } catch {
            // Mesmo se falhar, seguimos para o dashboard; a sessão admin permanece.
        } finally {
            // Limpeza defensiva de chaves órfãs de versões antigas.
            localStorage.removeItem('adminOriginalToken');
            localStorage.removeItem('selectedTenantId');
            localStorage.removeItem('selectedTenantName');
            localStorage.removeItem('tenantId');
            localStorage.removeItem('tenant_id');
            window.location.assign('/dashboard');
        }
    }, [busy]);

    return (
        <div className="admin-tenant-banner">
            <span className="admin-tenant-banner__info">
                👁️ Visualizando como cliente: <strong>{tenantName}</strong>
            </span>
            <button type="button" className="admin-tenant-banner__btn" onClick={handleBack}>
                <FiArrowLeft size={14} />
                Voltar ao painel gerencial
            </button>
        </div>
    );
}

/* =========================================================
   WeatherWidget
   ========================================================= */
const ICON_MAP = {
    '01d': <WiDaySunny />,        '01n': <WiNightClear />,
    '02d': <WiDayCloudy />,       '02n': <WiNightAltCloudy />,
    '03d': <WiCloud />,           '03n': <WiCloud />,
    '04d': <WiCloudy />,          '04n': <WiCloudy />,
    '09d': <WiDayShowers />,      '09n': <WiNightAltShowers />,
    '10d': <WiDayRain />,         '10n': <WiNightAltRain />,
    '11d': <WiDayThunderstorm />, '11n': <WiNightAltThunderstorm />,
    '13d': <WiSnow />,            '13n': <WiSnow />,
    '50d': <WiFog />,             '50n': <WiFog />,
};
const getWeatherIcon = (code) => ICON_MAP[code] ?? <WiCloud />;

const DEFAULT_CITY   = 'São José dos Campos';
const MAX_RETRIES    = 4;
const RETRY_DELAY_MS = 3_000;

const WeatherWidget = () => {
    const containerRef = useRef(null);

    const [weatherData,  setWeatherData]  = useState(null);
    const [cityInput,    setCityInput]    = useState(() => localStorage.getItem('userCity') || DEFAULT_CITY);
    const [error,        setError]        = useState('');
    const [loading,      setLoading]      = useState(true);
    const [retrying,     setRetrying]     = useState(false);
    const [retryCount,   setRetryCount]   = useState(0);
    const [suggestions,  setSuggestions]  = useState([]);
    const [open,         setOpen]         = useState(false);
    const [activeIndex,  setActiveIndex]  = useState(-1);

    const debouncedCity          = useDebouncedValue(cityInput, 300);
    const [, startTransition]    = useTransition();
    const weatherReqRef          = useRef(null);
    const geoReqRef              = useRef(null);
    const retryTimerRef          = useRef(null);
    const retryCountRef          = useRef(0);

    useEffect(() => () => {
        weatherReqRef.current?.abort();
        geoReqRef.current?.abort();
        clearTimeout(retryTimerRef.current);
    }, []);

    const closeSuggestions = useCallback(() => {
        setOpen(false);
        setActiveIndex(-1);
    }, []);
    useOnClickOutside(containerRef, closeSuggestions);

    const fetchWeather = useCallback(async (location, signal, silent = false) => {
        if (!silent) { setLoading(true); setError(''); setRetrying(false); }
        setOpen(false);
        let endpoint   = '';
        let cityToSave = '';
        try {
            if (typeof location === 'string') {
                const cityNameOnly = location.split(',')[0].trim();
                endpoint   = `/api/dashboard/weather?city=${encodeURIComponent(cityNameOnly)}`;
                cityToSave = location;
            } else if (location?.lat && location?.lon) {
                endpoint = `/api/dashboard/weather?lat=${location.lat}&lon=${location.lon}`;
            } else {
                throw new Error('Localização inválida.');
            }
            const { data } = await api.get(endpoint, { signal });
            if (signal?.aborted) return;
            setWeatherData(data);
            setError('');
            setRetrying(false);
            setRetryCount(0);
            retryCountRef.current = 0;
            const resolvedCity = data?.city || cityToSave || '';
            if (resolvedCity) {
                setCityInput(resolvedCity);
                localStorage.setItem('userCity', resolvedCity);
            }
        } catch (err) {
            if (signal?.aborted) return;
            if (!silent) {
                const isRetryable =
                    err?.response?.status === 500 ||
                    err?.response?.status === 503 ||
                    err?.response?.status === 504 ||
                    err?.code === 'ECONNABORTED'  ||
                    !err?.response;
                const current = retryCountRef.current;
                if (isRetryable && current < MAX_RETRIES) {
                    setRetrying(true);
                    setRetryCount(current + 1);
                    retryCountRef.current = current + 1;
                } else {
                    setRetrying(false);
                    setError(err?.response?.data?.error || 'Erro ao buscar clima. Tente outra cidade.');
                    if (!weatherData) localStorage.removeItem('userCity');
                }
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weatherData]);

    const runFetchWeather = useCallback((location, silent = false) => {
        const ctrl = new AbortController();
        if (silent) { geoReqRef.current?.abort();    geoReqRef.current    = ctrl; }
        else        { weatherReqRef.current?.abort(); weatherReqRef.current = ctrl; }
        startTransition(() => fetchWeather(location, ctrl.signal, silent));
    }, [fetchWeather, startTransition]);

    useEffect(() => {
        if (!retrying) return;
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
            const savedCity = localStorage.getItem('userCity') || DEFAULT_CITY;
            runFetchWeather(savedCity);
        }, RETRY_DELAY_MS);
        return () => clearTimeout(retryTimerRef.current);
    }, [retrying, retryCount, runFetchWeather]);

    useEffect(() => {
        const savedCity = localStorage.getItem('userCity') || DEFAULT_CITY;
        runFetchWeather(savedCity);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                ({ coords }) => runFetchWeather({ lat: coords.latitude, lon: coords.longitude }, true),
                () => {},
                { timeout: 6_000, maximumAge: 60_000 }
            );
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchGeolocationManual = useCallback(() => {
        setLoading(true); setError(''); setRetrying(false); setRetryCount(0);
        retryCountRef.current = 0;
        if (!navigator.geolocation) {
            setError('Geolocalização não suportada.'); setLoading(false); return;
        }
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => runFetchWeather({ lat: coords.latitude, lon: coords.longitude }),
            () => { setError('Localização não permitida. Digite a cidade.'); setLoading(false); },
            { timeout: 8_000 }
        );
    }, [runFetchWeather]);

    useEffect(() => {
        let active = true;
        const ctrl = new AbortController();
        async function run() {
            if (!debouncedCity || debouncedCity.length < 3) {
                if (active) { setSuggestions([]); setOpen(false); }
                return;
            }
            const saved = localStorage.getItem('userCity') || '';
            if (debouncedCity === saved) {
                if (active) { setSuggestions([]); setOpen(false); }
                return;
            }
            try {
                const { data } = await api.get(
                    `/api/cities/autocomplete?query=${encodeURIComponent(debouncedCity)}`,
                    { signal: ctrl.signal }
                );
                if (!active) return;
                const list = Array.isArray(data) ? data : [];
                setSuggestions(list);
                setOpen(list.length > 0);
                setActiveIndex(-1);
            } catch {
                if (!ctrl.signal.aborted && active) { setSuggestions([]); setOpen(false); }
            }
        }
        run();
        return () => { active = false; ctrl.abort(); };
    }, [debouncedCity]);

    const onSubmit = useCallback((e) => {
        e.preventDefault();
        const value = cityInput.trim();
        if (!value) return;
        setRetryCount(0); retryCountRef.current = 0;
        setRetrying(false); setError('');
        runFetchWeather(value);
        closeSuggestions();
    }, [cityInput, runFetchWeather, closeSuggestions]);

    const onSelectSuggestion = useCallback((city) => {
        runFetchWeather(city);
        closeSuggestions();
    }, [runFetchWeather, closeSuggestions]);

    const onKeyDown = (e) => {
        if (!open || !suggestions.length) return;
        if      (e.key === 'ArrowDown')                 { e.preventDefault(); setActiveIndex(i => (i + 1) % suggestions.length); }
        else if (e.key === 'ArrowUp')                   { e.preventDefault(); setActiveIndex(i => i <= 0 ? suggestions.length - 1 : i - 1); }
        else if (e.key === 'Enter' && activeIndex >= 0) { e.preventDefault(); onSelectSuggestion(suggestions[activeIndex]); }
        else if (e.key === 'Escape')                    { closeSuggestions(); }
    };

    const handleManualRetry = useCallback(() => {
        setRetryCount(0); retryCountRef.current = 0;
        setRetrying(false); setError('');
        const savedCity = localStorage.getItem('userCity') || DEFAULT_CITY;
        runFetchWeather(savedCity);
    }, [runFetchWeather]);

    const isFirstLoad = loading && !retrying && !weatherData;
    const isRetrying  = retrying || (loading && retryCount > 0);

    return (
        <div className="weather-container" ref={containerRef}>
            {isFirstLoad && (
                <div className="weather-widget-placeholder">
                    <span className="weather-skeleton" />
                </div>
            )}
            {isRetrying && (
                <div className="weather-retry-box" title={`Tentativa ${retryCount} de ${MAX_RETRIES}`}>
                    <span className="weather-retry-spinner" aria-hidden>⟳</span>
                    <span className="weather-retry-label">
                        Reconectando
                        <span className="weather-retry-dots">
                            <span>.</span><span>.</span><span>.</span>
                        </span>
                    </span>
                    <span className="weather-retry-count">{retryCount}/{MAX_RETRIES}</span>
                </div>
            )}
            {!isFirstLoad && !isRetrying && weatherData && (
                <div className="weather-widget">
                    <div className="weather-icon">{getWeatherIcon(weatherData.icon)}</div>
                    <div className="weather-info">
                        <span className="weather-temp">{weatherData.temp}°C</span>
                        <span className="weather-desc">{weatherData.description}</span>
                    </div>
                </div>
            )}
            {!isFirstLoad && !isRetrying && error && !weatherData && (
                <button type="button" className="weather-retry-manual" onClick={handleManualRetry} title={error}>
                    <span style={{ fontSize: '1.1rem' }}>⟳</span>
                    Tentar novamente
                </button>
            )}
            <form onSubmit={onSubmit} className="weather-form" role="search" aria-label="Buscar clima por cidade">
                <input
                    type="text"
                    value={cityInput}
                    onChange={(e) => setCityInput(e.target.value)}
                    onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
                    onKeyDown={onKeyDown}
                    placeholder={error && !weatherData ? error : 'Digite uma cidade...'}
                    className={error && !weatherData ? 'input-error' : ''}
                    autoComplete="off"
                    aria-autocomplete="list"
                    aria-expanded={open}
                    aria-controls="weather-suggestions"
                />
                <button type="button" className="location-button" onClick={fetchGeolocationManual}
                    title="Usar minha localização atual" aria-label="Usar minha localização">
                    <FiMapPin />
                </button>
                {cityInput && (
                    <button type="button" className="clear-button"
                        onClick={() => { setCityInput(''); setSuggestions([]); setOpen(false); setError(''); }}
                        aria-label="Limpar cidade">
                        &times;
                    </button>
                )}
                <button type="submit" className="search-button" aria-label="Buscar cidade">
                    <FiSearch />
                </button>
                {open && suggestions.length > 0 && (
                    <ul id="weather-suggestions" role="listbox" className="suggestions-list">
                        {suggestions.map((city, idx) => (
                            <li key={`${city}-${idx}`} role="option" aria-selected={idx === activeIndex}
                                className={idx === activeIndex ? 'active' : ''}
                                onMouseDown={() => onSelectSuggestion(city)}>
                                {city}
                            </li>
                        ))}
                    </ul>
                )}
            </form>
        </div>
    );
};

/* =========================================================
   NewsTicker
   ========================================================= */
function normalizeNewsItems(news) {
    if (!Array.isArray(news)) return [];
    return news.map((item) => {
        if (!item) return null;
        if (typeof item === 'string') return { title: item, source: '' };
        if (typeof item === 'object') {
            const title  = item.title || item.titulo || '';
            const source = item.source || item.fonte || item.publisher || '';
            return title ? { title, source } : null;
        }
        return null;
    }).filter(Boolean);
}

function chunk(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

const NewsTicker = memo(function NewsTicker() {
    const [now,       setNow]       = useState(() => new Date());
    const [dolar,     setDolar]     = useState(null);
    const [news,      setNews]      = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const ctrl = new AbortController();
        (async () => {
            try {
                const { data } = await api.get('/api/dashboard/ticker-data', { signal: ctrl.signal });
                setDolar(data?.dolar ?? null);
                setNews(Array.isArray(data?.news) ? data.news : []);
            } catch (e) {
                if (!ctrl.signal.aborted) console.error('Ticker:', e);
            } finally {
                if (!ctrl.signal.aborted) setIsLoading(false);
            }
        })();
        return () => ctrl.abort();
    }, []);

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 60_000);
        return () => clearInterval(t);
    }, []);

    const formattedDate = useMemo(
        () => now.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }),
        [now]
    );
    const formattedTime = useMemo(
        () => now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        [now]
    );

    const tickerItems = useMemo(() => {
        if (isLoading) return ['Carregando cotações e informações...'];
        const normalizedNews = normalizeNewsItems(news);
        const formattedNews  = normalizedNews.map((n) =>
            n.source ? `${String(n.source).toUpperCase()}: ${n.title}` : n.title
        );
        const groups = chunk(formattedNews, 5);
        const items  = [];
        if (groups[0]?.length) items.push(...groups[0]);
        items.push(`SISTEMA  |  ${formattedDate}  —  ${formattedTime}`);
        if (groups[1]?.length) items.push(...groups[1]);
        if (dolar) items.push(`DÓLAR: R$ ${dolar}`);
        for (let i = 2; i < groups.length; i++) items.push(...groups[i]);
        if (!formattedNews.length) {
            return [
                `SISTEMA  |  ${formattedDate}  —  ${formattedTime}`,
                ...(dolar ? [`DÓLAR: R$ ${dolar}`] : []),
            ];
        }
        return items;
    }, [news, dolar, formattedDate, formattedTime, isLoading]);

    return (
        <div className="news-ticker" aria-live="polite">
            <div className="ticker-content">
                {tickerItems.map((item, i) => <span key={i}          className="ticker-item">{item}</span>)}
                {tickerItems.map((item, i) => <span key={`dup-${i}`} className="ticker-item" aria-hidden>{item}</span>)}
            </div>
        </div>
    );
});

/* =========================================================
   KpiPainel — ledger financeiro (Lucro em destaque + itens)
   ========================================================= */
const currencyBRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const KpiPainel = memo(function KpiPainel({ kpiData, tituloSufixo, isLoading, isVisible }) {
    if (isLoading) {
        return (
            <div className="kpi-ledger">
                <div className="kpi-ledger__hero">
                    <div className="skeleton-line" style={{ width: '55%', height: '13px', marginBottom: '14px' }} />
                    <div className="skeleton-line" style={{ width: '70%', height: '34px' }} />
                </div>
                <div className="kpi-ledger__row">
                    {[0, 1, 2].map(i => (
                        <div className="kpi-ledger__item" key={i}>
                            <div className="skeleton-line" style={{ width: '75%', height: '11px', marginBottom: '10px' }} />
                            <div className="skeleton-line" style={{ width: '55%', height: '18px' }} />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const lucro       = kpiData?.lucro ?? 0;
    const metaLucro   = kpiData?.metaLucro ?? 0;
    const positivo    = lucro >= 0;
    const progresso   = positivo && metaLucro > 0 ? Math.min(lucro / metaLucro, 1) : null;

    const itens = [
        { key: 'faturamento', label: `Faturamento ${tituloSufixo}`,        value: kpiData?.faturamento        ?? 0, isCurrency: true },
        { key: 'despesas',    label: `Despesas ${tituloSufixo}`,           value: kpiData?.despesas           ?? 0, isCurrency: true },
        { key: 'servicos',    label: `Serviços concluídos ${tituloSufixo}`, value: kpiData?.servicosConcluidos ?? 0, isCurrency: false },
    ];

    return (
        <div className="kpi-ledger" role="group" aria-label="Indicadores financeiros e operacionais">
            <div className="kpi-ledger__hero">
                <span className="kpi-ledger__hero-label">Lucro {tituloSufixo}</span>
                <span className={`kpi-ledger__hero-value ${positivo ? 'is-positive' : 'is-negative'} ${!isVisible ? 'is-masked' : ''}`}>
                    {isVisible ? currencyBRL.format(lucro) : '•••••'}
                </span>
                {isVisible && progresso !== null && (
                    <div className="kpi-ledger__meta">
                        <div className="kpi-ledger__meta-track">
                            <div className="kpi-ledger__meta-fill" style={{ width: `${Math.round(progresso * 100)}%` }} />
                        </div>
                        <span className="kpi-ledger__meta-label">
                            {Math.round(progresso * 100)}% da meta de {currencyBRL.format(metaLucro)}
                        </span>
                    </div>
                )}
            </div>
            <div className="kpi-ledger__row">
                {itens.map(item => (
                    <div className="kpi-ledger__item" key={item.key}>
                        <span className="kpi-ledger__item-label">{item.label}</span>
                        <span className={`kpi-ledger__item-value ${!isVisible ? 'is-masked' : ''}`}>
                            {isVisible ? (item.isCurrency ? currencyBRL.format(item.value) : item.value) : '•••••'}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
});

/* =========================================================
   AboutSection
   ========================================================= */
const AboutSection = memo(function AboutSection({ title, text, isLoading }) {
    if (isLoading) {
        return (
            <div className="about-section">
                <div className="skeleton-line" style={{ width: '50%', height: '22px', marginBottom: '12px' }} />
                <div className="skeleton-line" style={{ width: '100%', height: '14px', marginBottom: '6px' }} />
                <div className="skeleton-line" style={{ width: '90%',  height: '14px', marginBottom: '6px' }} />
                <div className="skeleton-line" style={{ width: '75%',  height: '14px' }} />
            </div>
        );
    }
    return (
        <div className="about-section">
            <h2 className={!title ? 'tenant-placeholder' : ''}>
                {!title ? '⚙️ Título não configurado' : title}
            </h2>
            <p className={!text ? 'tenant-placeholder' : ''}>
                {!text
                    ? 'Nenhuma descrição institucional foi cadastrada. Acesse SystemHub → Clientes → Personalização.'
                    : text}
            </p>
        </div>
    );
});

/* =========================================================
   AgendaDayModal
   ========================================================= */
const AgendaDayModal = memo(function AgendaDayModal({
    data, eventos, os, loading, onClose, onNovoEvento, onToggleConcluido, onDeleteEvento,
}) {
    const [expandido, setExpandido] = useState(null);

    const dataLabel = useMemo(() => {
        if (!data) return '';
        return new Date(data + 'T12:00:00').toLocaleDateString('pt-BR', {
            weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        });
    }, [data]);

    const itensOrdenados = useMemo(() => {
        const pessoais = eventos.map(e => ({ ...e, _origem: 'pessoal' }));
        const ordens   = os.map(o => ({
            id       : `os-${o.id}`,
            titulo   : `OS #${o.id} — ${o.cliente_nome || 'Cliente'}`,
            data_hora: o.data_hora,
            tipo     : 'os',
            cor      : '#6366f1',
            concluido: false,
            _origem  : 'os',
            _os      : o,
        }));
        return [...pessoais, ...ordens].sort((a, b) => new Date(a.data_hora) - new Date(b.data_hora));
    }, [eventos, os]);

    const pendentes = eventos.filter(e => !e.concluido).length;

    return (
        <div
            style={{
                position: 'fixed', inset: 0,
                backgroundColor: 'rgba(15,23,42,0.72)',
                backdropFilter: 'blur(5px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 8000, padding: '20px',
            }}
            onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{
                width: '100%', maxWidth: '540px', maxHeight: '88vh',
                backgroundColor: '#fff', borderRadius: '20px',
                boxShadow: '0 32px 72px -12px rgba(0,0,0,0.35)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                animation: 'entSlideUp 0.22s cubic-bezier(0.34,1.56,0.64,1)',
            }}>
                <div style={{
                    padding: '20px 24px',
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    flexShrink: 0,
                }}>
                    <div>
                        <p style={{ margin: 0, color: 'rgba(255,255,255,0.65)', fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em' }}>
                            Agenda Pessoal
                        </p>
                        <h3 style={{ margin: '4px 0 0', color: '#fff', fontSize: '1.05rem', fontWeight: 700, textTransform: 'capitalize' }}>
                            {dataLabel}
                        </h3>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                            onClick={onNovoEvento}
                            style={{
                                padding: '8px 16px', borderRadius: '8px', border: 'none',
                                backgroundColor: 'rgba(255,255,255,0.18)', color: '#fff',
                                fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem',
                                display: 'flex', alignItems: 'center', gap: '5px',
                                transition: 'background 0.18s',
                            }}
                            onMouseOver={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.28)'}
                            onMouseOut={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.18)'}
                        >
                            + Novo Evento
                        </button>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'rgba(255,255,255,0.14)', border: 'none',
                                color: '#fff', cursor: 'pointer', padding: '7px',
                                borderRadius: '50%', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', fontSize: '0.95rem', lineHeight: 1,
                                transition: 'background 0.18s',
                            }}
                            onMouseOver={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.24)'}
                            onMouseOut={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.14)'}
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
                            <div style={{ fontSize: '1.6rem', marginBottom: '10px' }}>⏳</div>
                            <p style={{ margin: 0 }}>Carregando agenda...</p>
                        </div>
                    ) : itensOrdenados.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📭</div>
                            <p style={{ margin: 0, fontWeight: 700, color: '#64748b' }}>Dia livre!</p>
                            <p style={{ margin: '6px 0 0', fontSize: '0.82rem' }}>
                                Clique em "Novo Evento" para adicionar um compromisso.
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {itensOrdenados.map((item) => {
                                const isExpanded = expandido === item.id;
                                const tipoConf   = TIPO_CONFIG[item.tipo] ?? { label: item.tipo, emoji: '📅', bg: '#f1f5f9', color: '#475569' };
                                const isOS       = item._origem === 'os';

                                return (
                                    <div
                                        key={item.id}
                                        style={{
                                            borderRadius: '12px',
                                            border: `1px solid ${isExpanded ? (item.cor || '#4f46e5') + '40' : '#e2e8f0'}`,
                                            backgroundColor: item.concluido ? '#f8fafc' : '#fff',
                                            overflow: 'hidden', transition: 'all 0.18s',
                                            opacity: item.concluido ? 0.6 : 1,
                                            boxShadow: isExpanded ? `0 4px 16px -4px ${(item.cor || '#4f46e5')}30` : '0 1px 2px rgba(0,0,0,0.04)',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '11px',
                                                padding: '13px 15px', cursor: 'pointer',
                                                borderLeft: `4px solid ${item.cor || '#4f46e5'}`,
                                            }}
                                            onClick={() => setExpandido(isExpanded ? null : item.id)}
                                        >
                                            {!isOS ? (
                                                <input
                                                    type="checkbox"
                                                    checked={!!item.concluido}
                                                    onChange={e => { e.stopPropagation(); onToggleConcluido(item.id, !item.concluido, item); }}
                                                    onClick={e => e.stopPropagation()}
                                                    style={{ width: '17px', height: '17px', accentColor: item.cor, cursor: 'pointer', flexShrink: 0 }}
                                                />
                                            ) : (
                                                <span style={{ fontSize: '1rem', flexShrink: 0 }}>🔧</span>
                                            )}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                                                    <span style={{
                                                        fontWeight: 600, color: '#0f172a', fontSize: '0.9rem',
                                                        textDecoration: item.concluido ? 'line-through' : 'none',
                                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '190px',
                                                    }}>
                                                        {item.titulo}
                                                    </span>
                                                    <span style={{
                                                        padding: '1px 7px', borderRadius: '99px', fontSize: '0.68rem',
                                                        fontWeight: 700, textTransform: 'uppercase',
                                                        backgroundColor: isOS ? '#e0e7ff' : tipoConf.bg,
                                                        color: isOS ? '#4338ca' : tipoConf.color, flexShrink: 0,
                                                    }}>
                                                        {isOS ? '🔧 OS' : `${tipoConf.emoji} ${tipoConf.label}`}
                                                    </span>
                                                </div>
                                                <span style={{ fontSize: '0.77rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '3px', marginTop: '3px' }}>
                                                    🕐 {fmtHora(item.data_hora)}
                                                    {item.data_hora_fim && ` → ${fmtHora(item.data_hora_fim)}`}
                                                </span>
                                            </div>
                                            {!isOS && (
                                                <button
                                                    onClick={e => { e.stopPropagation(); onDeleteEvento(item.id); }}
                                                    style={{
                                                        background: 'transparent', border: 'none', color: '#cbd5e1',
                                                        cursor: 'pointer', padding: '4px', borderRadius: '6px',
                                                        fontSize: '0.9rem', transition: 'color 0.15s', flexShrink: 0,
                                                    }}
                                                    onMouseOver={e => e.currentTarget.style.color = '#ef4444'}
                                                    onMouseOut={e => e.currentTarget.style.color = '#cbd5e1'}
                                                    title="Remover evento"
                                                >
                                                    🗑️
                                                </button>
                                            )}
                                            <span style={{ color: '#94a3b8', fontSize: '0.75rem', flexShrink: 0, userSelect: 'none' }}>
                                                {isExpanded ? '▲' : '▼'}
                                            </span>
                                        </div>
                                        {isExpanded && (
                                            <div style={{
                                                padding: '10px 15px 13px',
                                                borderTop: `1px dashed ${(item.cor || '#4f46e5')}30`,
                                                backgroundColor: '#fafbff',
                                                animation: 'entSlideUp 0.15s ease-out',
                                            }}>
                                                {item.descricao && (
                                                    <p style={{ margin: '0 0 7px', color: '#475569', fontSize: '0.85rem', lineHeight: 1.5 }}>
                                                        {item.descricao}
                                                    </p>
                                                )}
                                                {!isOS && item.lembrete_min > 0 && (
                                                    <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.77rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        🔔 Lembrete configurado: <strong>{item.lembrete_min} min antes</strong>
                                                    </p>
                                                )}
                                                {isOS && item._os && (
                                                    <div style={{ fontSize: '0.82rem', color: '#475569', display: 'flex', gap: '16px' }}>
                                                        <span>Status: <strong>{item._os.status}</strong></span>
                                                        {item._os.valor && (
                                                            <span>Valor: <strong>R$ {Number(item._os.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>
                                                        )}
                                                    </div>
                                                )}
                                                {!item.descricao && !item.lembrete_min && !isOS && (
                                                    <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                                        Sem detalhes adicionais.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div style={{
                    padding: '12px 20px', borderTop: '1px solid #e2e8f0',
                    backgroundColor: '#f8fafc', display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
                }}>
                    <span style={{ fontSize: '0.77rem', color: '#94a3b8' }}>
                        {itensOrdenados.length} item(s)
                        {pendentes > 0 && ` · ${pendentes} pendente(s)`}
                    </span>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px 20px', borderRadius: '8px',
                            border: '1px solid #e2e8f0', backgroundColor: '#fff',
                            color: '#475569', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
                        }}
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
});

/* =========================================================
   NovoEventoModal
   ========================================================= */
const NovoEventoModal = memo(function NovoEventoModal({ data, onClose, onSalvo }) {
    const [form, setForm] = useState({
        titulo       : '',
        tipo         : 'evento',
        data_hora    : data ? `${data}T09:00` : '',
        data_hora_fim: data ? `${data}T10:00` : '',
        cor          : '#4f46e5',
        lembrete_min : 30,
        descricao    : '',
    });
    const [salvando, setSalvando] = useState(false);
    const [erro,     setErro]     = useState('');
    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const handleSalvar = async () => {
        if (!form.titulo.trim()) { setErro('O título é obrigatório.'); return; }
        if (!form.data_hora)     { setErro('Data e hora são obrigatórias.'); return; }
        setSalvando(true); setErro('');
        try {
            await api.post('/api/agenda/evento', form);
            onSalvo();           // recarrega calendário
            notifyAgendaUpdate(); // ← dispara o evento global para o widget
            onClose();
        } catch (e) {
            setErro(e?.response?.data?.error || 'Erro ao salvar. Tente novamente.');
        } finally {
            setSalvando(false);
        }
    };

    const inputSt = {
        width: '100%', padding: '10px 12px', borderRadius: '8px',
        border: '1px solid #cbd5e1', fontSize: '0.88rem', color: '#334155',
        backgroundColor: '#f8fafc', outline: 'none', boxSizing: 'border-box',
        fontFamily: 'inherit', transition: 'border-color 0.15s',
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0,
                backgroundColor: 'rgba(15,23,42,0.8)', backdropFilter: 'blur(5px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 9000, padding: '20px',
            }}
            onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{
                width: '100%', maxWidth: '440px', backgroundColor: '#fff',
                borderRadius: '18px', boxShadow: '0 32px 72px -12px rgba(0,0,0,0.35)',
                overflow: 'hidden',
                animation: 'entSlideUp 0.22s cubic-bezier(0.34,1.56,0.64,1)',
            }}>
                <div style={{ padding: '18px 22px', background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}>
                    <h3 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '1.05rem' }}>📅 Novo Evento</h3>
                </div>
                <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {erro && (
                        <div style={{ padding: '9px 13px', backgroundColor: '#fee2e2', borderRadius: '8px', color: '#dc2626', fontSize: '0.82rem' }}>
                            ⚠️ {erro}
                        </div>
                    )}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Título *</label>
                        <input type="text" style={inputSt} placeholder="Ex: Reunião com cliente..." value={form.titulo}
                            onChange={e => set('titulo', e.target.value)} maxLength={200} autoFocus
                            onFocus={e => e.target.style.borderColor = '#4f46e5'}
                            onBlur={e => e.target.style.borderColor = '#cbd5e1'} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Tipo</label>
                            <select style={{ ...inputSt, cursor: 'pointer' }} value={form.tipo} onChange={e => set('tipo', e.target.value)}>
                                <option value="evento">📌 Evento</option>
                                <option value="reuniao">👥 Reunião</option>
                                <option value="lembrete">🔔 Lembrete</option>
                                <option value="prazo">⏰ Prazo</option>
                                <option value="pessoal">🏠 Pessoal</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lembrete</label>
                            <select style={{ ...inputSt, cursor: 'pointer' }} value={form.lembrete_min} onChange={e => set('lembrete_min', parseInt(e.target.value, 10))}>
                                <option value={0}>Sem lembrete</option>
                                <option value={10}>10 min antes</option>
                                <option value={30}>30 min antes</option>
                                <option value={60}>1 hora antes</option>
                                <option value={1440}>1 dia antes</option>
                            </select>
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Início *</label>
                            <input type="datetime-local" style={inputSt} value={form.data_hora}
                                onChange={e => set('data_hora', e.target.value)}
                                onFocus={e => e.target.style.borderColor = '#4f46e5'}
                                onBlur={e => e.target.style.borderColor = '#cbd5e1'} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Término</label>
                            <input type="datetime-local" style={inputSt} value={form.data_hora_fim}
                                onChange={e => set('data_hora_fim', e.target.value)}
                                onFocus={e => e.target.style.borderColor = '#4f46e5'}
                                onBlur={e => e.target.style.borderColor = '#cbd5e1'} />
                        </div>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Cor do Evento</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {CORES_PALETA.map(c => (
                                <button key={c} onClick={() => set('cor', c)} style={{
                                    width: '26px', height: '26px', borderRadius: '50%', backgroundColor: c,
                                    border: form.cor === c ? '3px solid #0f172a' : '3px solid transparent',
                                    cursor: 'pointer',
                                    transform: form.cor === c ? 'scale(1.22)' : 'scale(1)',
                                    transition: 'transform 0.15s, border 0.15s',
                                    boxShadow: form.cor === c ? `0 0 0 2px ${c}60` : 'none',
                                }} title={c} />
                            ))}
                        </div>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Descrição (opcional)</label>
                        <textarea style={{ ...inputSt, resize: 'vertical', minHeight: '68px' }}
                            placeholder="Local, link da videoconferência, observações..."
                            value={form.descricao} onChange={e => set('descricao', e.target.value)} maxLength={1000}
                            onFocus={e => e.target.style.borderColor = '#4f46e5'}
                            onBlur={e => e.target.style.borderColor = '#cbd5e1'} />
                    </div>
                </div>
                <div style={{ padding: '14px 22px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', backgroundColor: '#f8fafc' }}>
                    <button onClick={onClose} disabled={salvando} style={{ padding: '9px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' }}>
                        Cancelar
                    </button>
                    <button onClick={handleSalvar} disabled={salvando} style={{ padding: '9px 22px', borderRadius: '8px', border: 'none', background: salvando ? '#a5b4fc' : 'linear-gradient(135deg, #4f46e5, #7c3aed)', color: '#fff', fontWeight: 700, cursor: salvando ? 'wait' : 'pointer', fontSize: '0.88rem' }}>
                        {salvando ? '⏳ Salvando...' : '✅ Salvar Evento'}
                    </button>
                </div>
            </div>
        </div>
    );
});

/* =========================================================
   ProximoEventoWidget — Pill inline no header
   Usa useProximosEventos para polling em camadas.
   ========================================================= */
const ProximoEventoWidget = memo(function ProximoEventoWidget() {
    const { proximos } = useProximosEventos();
    const [agora,    setAgora]   = useState(() => new Date());
    const [expanded, setExpanded] = useState(false);
    const alertadoRef = useRef(new Set());
    const wrapRef     = useRef(null);
    useOnClickOutside(wrapRef, () => setExpanded(false));

    // Tick por segundo para countdown em tempo real
    useEffect(() => {
        const t = setInterval(() => setAgora(new Date()), 1_000);
        return () => clearInterval(t);
    }, []);

    // Alerta sonoro no momento exato do lembrete
    useEffect(() => {
        proximos.forEach(ev => {
            const diffMin = Math.floor((new Date(ev.data_hora) - agora) / 60_000);
            const key     = `${ev.id}-${ev.lembrete_min}`;
            if (diffMin === ev.lembrete_min && !alertadoRef.current.has(key)) {
                alertadoRef.current.add(key);
                try { new Audio('/notification.mp3').play().catch(() => {}); } catch { /* ignora */ }
            }
        });
    }, [agora, proximos]);

    const proximo = proximos.find(ev => new Date(ev.data_hora) > agora);
    if (!proximo) return null;

    const diffMs   = new Date(proximo.data_hora) - agora;
    const diffMin  = Math.floor(diffMs / 60_000);
    const urgente  = diffMin < 30;
    const iminente = diffMin < 5;
    const corEvento = proximo.cor || '#4f46e5';
    const tipoConf  = TIPO_CONFIG[proximo.tipo] ?? TIPO_CONFIG.evento;

    const countdownDisplay = (() => {
        if (diffMs <= 0) return 'Agora!';
        const totalSec = Math.floor(diffMs / 1_000);
        const h = Math.floor(totalSec / 3_600);
        const m = Math.floor((totalSec % 3_600) / 60);
        const s = totalSec % 60;
        if (h >= 1) return `${h}h ${m}min`;
        return `${m}:${String(s).padStart(2, '0')}`;
    })();

    return (
        <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
            {/* Pill compacto */}
            <button
                onClick={() => setExpanded(p => !p)}
                style={{
                    display        : 'flex',
                    alignItems     : 'center',
                    gap            : '8px',
                    padding        : '7px 14px',
                    borderRadius   : '12px',
                    border         : `1.5px solid ${urgente ? '#fca5a5' : corEvento + '50'}`,
                    backgroundColor: urgente ? '#fef2f2' : corEvento + '12',
                    cursor         : 'pointer',
                    animation      : iminente ? 'pulseBadge 0.9s ease-in-out infinite' : 'none',
                    transition     : 'all 0.2s',
                    whiteSpace     : 'nowrap',
                }}
            >
                <span style={{ fontSize: '0.9rem' }}>{tipoConf.emoji}</span>
                <span style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {proximo.titulo}
                </span>
                <span style={{ fontWeight: 800, fontSize: '0.88rem', fontVariantNumeric: 'tabular-nums', color: urgente ? '#dc2626' : corEvento, letterSpacing: '-0.01em' }}>
                    {countdownDisplay}
                </span>
            </button>

            {/* Dropdown */}
            {expanded && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                    minWidth: '270px', backgroundColor: '#fff', borderRadius: '14px',
                    boxShadow: '0 16px 40px -8px rgba(0,0,0,0.22)',
                    border: `1px solid ${corEvento}30`, overflow: 'hidden',
                    zIndex: 500, animation: 'entSlideUp 0.18s ease-out',
                }}>
                    <div style={{ height: '3px', backgroundColor: corEvento }} />
                    <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <p style={{ margin: '0 0 4px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.06em' }}>
                            Próximos eventos hoje
                        </p>
                        {proximos.map((ev, i) => {
                            const tc     = TIPO_CONFIG[ev.tipo] ?? TIPO_CONFIG.evento;
                            const isNext = i === 0;
                            const evDiff = new Date(ev.data_hora) - agora;
                            return (
                                <div key={ev.id} style={{
                                    display: 'flex', alignItems: 'center', gap: '10px',
                                    padding: '9px 11px', borderRadius: '9px',
                                    backgroundColor: isNext ? corEvento + '10' : '#f8fafc',
                                    border: `1px solid ${isNext ? corEvento + '30' : '#e2e8f0'}`,
                                }}>
                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: ev.cor || '#4f46e5', flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.82rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {tc.emoji} {ev.titulo}
                                        </p>
                                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>
                                            {fmtHora(ev.data_hora)}
                                        </p>
                                    </div>
                                    {isNext && evDiff > 0 && (
                                        <span style={{ fontWeight: 800, fontSize: '0.82rem', color: urgente ? '#dc2626' : corEvento, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                            {countdownDisplay}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
});

/* =========================================================
   Dashboard Principal
   ========================================================= */
export default function Dashboard({ user }) {

    // [FASE 3] "Admin vendo um tenant?" agora vem do backend (current-tenant lê o
    // active_tenant_id do cookie), não mais de adminOriginalToken no localStorage.
    const isAdmin = user?.perfil === 'admin_geral';
    const [adminTenantView, setAdminTenantView] = useState({ active: false, nome: '' });
    const isAdminViewingTenant = adminTenantView.active;

    useEffect(() => {
        if (!isAdmin) return;
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get('/api/auth/current-tenant');
                if (alive && data?.tenant?.id) {
                    setAdminTenantView({ active: true, nome: data.tenant.nome_empresa || 'cliente' });
                } else if (alive) {
                    setAdminTenantView({ active: false, nome: '' });
                }
            } catch {
                if (alive) setAdminTenantView({ active: false, nome: '' });
            }
        })();
        return () => { alive = false; };
    }, [isAdmin]);

    const [autoAnuncio, setAutoAnuncio] = useState(null);

    useEffect(() => {
        const ctrl = new AbortController();
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get('/api/conteudo/anuncios-ativos', { signal: ctrl.signal });
                if (Array.isArray(data) && data.length > 0) setAutoAnuncio(data[0]);
            } catch (e) {
                if (!ctrl.signal.aborted) console.error('Anúncio:', e);
            }
        }, 1_500);
        return () => { clearTimeout(t); ctrl.abort(); };
    }, []);

    const [tenantConfig,  setTenantConfig]  = useState(null);
    const [loadingTenant, setLoadingTenant] = useState(true);

    useEffect(() => {
        const ctrl = new AbortController();
        (async () => {
            try {
                const { data } = await api.get('/api/customize/config', { signal: ctrl.signal });
                setTenantConfig(data);
            } catch (e) {
                if (!ctrl.signal.aborted) console.error('Tenant config:', e);
            } finally {
                if (!ctrl.signal.aborted) setLoadingTenant(false);
            }
        })();
        return () => ctrl.abort();
    }, []);

    const nomeEmpresa = tenantConfig?.sidebar_config?.label   || user?.nome_empresa || null;
    const aboutTitle  = tenantConfig?.dashboard_config?.title || null;
    const aboutText   = tenantConfig?.dashboard_config?.text  || null;

    // ── Agenda ────────────────────────────────────────────
    const [dataSelecionada,  setDataSelecionada]  = useState(new Date());
    const [dataAtiva,        setDataAtiva]        = useState(new Date());
    const [diasComEvento,    setDiasComEvento]    = useState([]);
    const [diasComOS,        setDiasComOS]        = useState([]);
    const [agendaModal,      setAgendaModal]      = useState(false);
    const [novoEventoOpen,   setNovoEventoOpen]   = useState(false);
    const [agendaDia,        setAgendaDia]        = useState({ eventos: [], os: [] });
    const [loadingAgendaDia, setLoadingAgendaDia] = useState(false);

    useEffect(() => {
        const ctrl = new AbortController();
        (async () => {
            try {
                const ano = dataAtiva.getFullYear();
                const mes = dataAtiva.getMonth() + 1;
                const { data } = await api.get(`/api/agenda/mes?ano=${ano}&mes=${mes}`, { signal: ctrl.signal });
                setDiasComEvento(Array.isArray(data.diasComEvento) ? data.diasComEvento : []);
                setDiasComOS(Array.isArray(data.diasComOS)         ? data.diasComOS     : []);
            } catch (e) {
                if (!ctrl.signal.aborted) console.error('Agenda/mes:', e);
            }
        })();
        return () => ctrl.abort();
    }, [dataAtiva]);

    const abrirAgendaDia = useCallback(async (date) => {
        setDataSelecionada(date);
        setAgendaModal(true);
        setLoadingAgendaDia(true);
        try {
            const { data } = await api.get(`/api/agenda/dia?data=${toISODateLocal(date)}`);
            setAgendaDia({
                eventos: Array.isArray(data.eventos) ? data.eventos : [],
                os     : Array.isArray(data.os)      ? data.os      : [],
            });
        } catch (e) {
            console.error('Agenda/dia:', e);
            setAgendaDia({ eventos: [], os: [] });
        } finally {
            setLoadingAgendaDia(false);
        }
    }, []);

    const recarregarAgenda = useCallback(async () => {
        if (!dataSelecionada) return;
        try {
            const [diaRes, mesRes] = await Promise.all([
                api.get(`/api/agenda/dia?data=${toISODateLocal(dataSelecionada)}`),
                api.get(`/api/agenda/mes?ano=${dataAtiva.getFullYear()}&mes=${dataAtiva.getMonth() + 1}`),
            ]);
            setAgendaDia({
                eventos: Array.isArray(diaRes.data.eventos) ? diaRes.data.eventos : [],
                os     : Array.isArray(diaRes.data.os)      ? diaRes.data.os      : [],
            });
            setDiasComEvento(mesRes.data.diasComEvento || []);
            setDiasComOS(mesRes.data.diasComOS || []);
        } catch (e) {
            console.error('Agenda reload:', e);
        }
        // Notifica o widget — evento customizado dispara re-fetch imediato
        notifyAgendaUpdate();
    }, [dataSelecionada, dataAtiva]);

    const handleToggleConcluido = useCallback(async (id, concluido, eventoAtual) => {
        setAgendaDia(prev => ({
            ...prev,
            eventos: prev.eventos.map(e => e.id === id ? { ...e, concluido: concluido ? 1 : 0 } : e),
        }));
        try {
            await api.put(`/api/agenda/evento/${id}`, { ...eventoAtual, concluido });
            notifyAgendaUpdate();
        } catch (e) {
            console.error('Toggle concluido:', e);
            recarregarAgenda();
        }
    }, [recarregarAgenda]);

    const handleDeleteEvento = useCallback(async (id) => {
        if (!window.confirm('Remover este evento da agenda?')) return;
        try {
            await api.delete(`/api/agenda/evento/${id}`);
            recarregarAgenda(); // já chama notifyAgendaUpdate internamente
        } catch (e) {
            console.error('Delete evento:', e);
        }
    }, [recarregarAgenda]);

    const marcarDias = useCallback(({ date, view }) => {
        if (view !== 'month') return null;
        const dia       = toISODateLocal(date);
        const temEvento = diasComEvento.includes(dia);
        const temOS     = diasComOS.includes(dia);
        if (!temEvento && !temOS) return null;
        return (
            <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', marginTop: '2px' }}>
                {temEvento && <span className="agenda-dot agenda-dot--evento" title="Eventos pessoais" />}
                {temOS     && <span className="agenda-dot agenda-dot--os"     title="OS agendadas"    />}
            </div>
        );
    }, [diasComEvento, diasComOS]);

    // ── KPIs ──────────────────────────────────────────────
    const [kpiData,         setKpiData]         = useState(null);
    const [loadingKpi,      setLoadingKpi]      = useState(true);
    const [errorKpi,        setErrorKpi]        = useState(null);
    const [valoresVisiveis, setValoresVisiveis] = useState(true);
    const [periodoKpi,      setPeriodoKpi]      = useState('mensal');

    const fetchResumo = useCallback(async (periodoSelecionado, silent = false) => {
        if (!silent) setLoadingKpi(true);
        try {
            const { data } = await api.get(`/api/dashboard/resumo?periodo=${periodoSelecionado}`);
            setKpiData(data);
            setErrorKpi(null);
        } catch (e) {
            console.error('KPIs:', e);
            setErrorKpi('Não foi possível carregar os indicadores.');
        } finally {
            setLoadingKpi(false);
        }
    }, []);

    useEffect(() => {
        fetchResumo(periodoKpi);
        const onFocus = () => fetchResumo(periodoKpi, true);
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [fetchResumo, periodoKpi]);

    const firstName    = useMemo(() => user?.nome?.split(' ')[0] || 'Admin', [user]);
    const tituloSufixo = periodoKpi === 'mensal' ? '(Mensal)' : '(Anual)';

    const welcomeSubtitle = useMemo(() => {
        if (loadingTenant) return 'Carregando dados da empresa...';
        if (nomeEmpresa)   return `Aqui está o resumo do seu dia na ${nomeEmpresa}.`;
        return 'Aqui está o resumo do seu dia.';
    }, [loadingTenant, nomeEmpresa]);

    return (
        <div className="dashboard-container dashboard-container--main">

            {isAdminViewingTenant && <AdminTenantBanner tenantName={adminTenantView.nome} />}

            {autoAnuncio && (
                <AnuncioModal isVisible={true} anuncio={autoAnuncio} onClose={() => setAutoAnuncio(null)} />
            )}

            {/* Header: welcome | próximo evento | clima */}
            <header className="dashboard-header">
                <div className="welcome-message">
                    <h1>Bem-vindo, {firstName}!</h1>
                    <p className={!nomeEmpresa && !loadingTenant ? 'tenant-placeholder' : ''}>
                        {welcomeSubtitle}
                    </p>
                </div>
                <ProximoEventoWidget />
                <WeatherWidget />
            </header>

            {errorKpi && <div className="error-message" role="alert">{errorKpi}</div>}

            <div className="kpi-grid-container kpi-grid-container--dashboard">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {['mensal', 'anual'].map(p => (
                            <button key={p} onClick={() => setPeriodoKpi(p)} style={{
                                padding: '6px 16px', borderRadius: '6px',
                                border: '1px solid var(--border-color, #ccc)',
                                backgroundColor: periodoKpi === p ? 'var(--primary-color, #0d6efd)' : 'var(--bg-card, #fff)',
                                color: periodoKpi === p ? '#fff' : 'var(--text-color, #333)',
                                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', textTransform: 'capitalize',
                            }}>
                                {p === 'mensal' ? 'Mensal' : 'Anual'}
                            </button>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => fetchResumo(periodoKpi, false)} className="toggle-visibility-btn" title="Atualizar painel"><FaRedoAlt /></button>
                        <button onClick={() => setValoresVisiveis(v => !v)} className="toggle-visibility-btn"
                            title={valoresVisiveis ? 'Ocultar valores' : 'Mostrar valores'} aria-pressed={valoresVisiveis}>
                            {valoresVisiveis ? <FaEye /> : <FaEyeSlash />}
                        </button>
                    </div>
                </div>
                <KpiPainel kpiData={kpiData} tituloSufixo={tituloSufixo} isLoading={loadingKpi} isVisible={valoresVisiveis} />
            </div>

            <main className="dashboard-main-content">
                <div className="agenda-card">
                    <h2>Agenda de Compromissos</h2>
                    <Calendar
                        onClickDay={abrirAgendaDia}
                        value={dataSelecionada}
                        className="react-calendar-custom"
                        tileContent={marcarDias}
                        onActiveStartDateChange={({ activeStartDate }) => setDataAtiva(activeStartDate)}
                    />
                    <p className="agenda-hint">
                        <span className="agenda-dot agenda-dot--evento" style={{ display: 'inline-block', marginRight: '4px', verticalAlign: 'middle' }} />
                        Eventos pessoais &nbsp;·&nbsp;
                        <span className="agenda-dot agenda-dot--os" style={{ display: 'inline-block', marginRight: '4px', verticalAlign: 'middle' }} />
                        OS agendadas &nbsp;·&nbsp; Clique em um dia para gerenciar
                    </p>
                </div>
                <div className="content-card">
                    <CoverflowSlideshow />
                    <AboutSection title={aboutTitle} text={aboutText} isLoading={loadingTenant} />
                </div>
            </main>

            <NewsTicker />

            {agendaModal && (
                <AgendaDayModal
                    data={toISODateLocal(dataSelecionada)}
                    eventos={agendaDia.eventos}
                    os={agendaDia.os}
                    loading={loadingAgendaDia}
                    onClose={() => setAgendaModal(false)}
                    onNovoEvento={() => setNovoEventoOpen(true)}
                    onToggleConcluido={handleToggleConcluido}
                    onDeleteEvento={handleDeleteEvento}
                />
            )}

            {novoEventoOpen && (
                <NovoEventoModal
                    data={toISODateLocal(dataSelecionada)}
                    onClose={() => setNovoEventoOpen(false)}
                    onSalvo={recarregarAgenda}
                />
            )}
        </div>
    );
}